import type { PQueryBuilderField } from '@pushly/models/lib/interfaces/pquery-builder-field'
import type { PageTagScope } from './page-tag-source'
import { createSegmentFieldValueSource } from './segment-field-value-source'
import { createKeywordValueSource } from './keyword-value-source'

/**
 * The one catalog field whose values are a list the domain curates rather than values observed on
 * subscribers, and so the one field the values table cannot answer for.
 */
const KEYWORD_FIELD_KEY = 'tag'

/**
 * How to ask this loader for a keyword, for a control that wants one but is not the keyword field.
 *
 * The frequency conditions carry a keyword in `meta.expression` on an integer field, so their
 * pickers have no field descriptor of their own to hand over — but they want the identical list,
 * paged and searched the identical way. Reading it through the same loader is what makes the two
 * behave the same rather than merely look like they do.
 *
 * A module constant, not a value built per render: the reader keyed on it treats a new descriptor as
 * a new field and starts the list over.
 */
export const KEYWORD_VALUE_FIELD: PQueryBuilderField = {
    key: KEYWORD_FIELD_KEY,
    label: 'Keyword',
    type: 'text',
    // Says the values are not carried inline and are to be asked for. Without it the reader offers
    // no search and no paging, and the control sits empty.
    optionsTruncated: true,
}

export type FieldValueLoader = (
    field: PQueryBuilderField,
    search: string,
    page: { offset: number; limit: number; signal: AbortSignal },
) => Promise<{ label: string; value: string }[]>

/**
 * Answers "what values can this field hold", wherever the answer happens to live.
 *
 * The builder owns the controls that read a field's values but not the endpoints they come from, so it
 * takes this as an injected read. Keeping the choice of source here rather than in the builder is what
 * lets the keyword field draw on its own list without the builder learning that any field is special.
 *
 * Both sources are asked in rows, ordered by how many subscribers hold each value, so a caller pages
 * them identically and the difference stays inside.
 */
export function createFieldValueLoader(scope: PageTagScope): FieldValueLoader {
    const subscriberValues = createSegmentFieldValueSource(scope)
    const keywords = createKeywordValueSource(scope)

    return async (field, search, page) => {
        const source = field.key === KEYWORD_FIELD_KEY ? keywords : subscriberValues
        const values = await source.read({ key: field.key, search, ...page })

        // A value is its own label: these are the subscriber's own values, or the domain's own
        // keywords, so there is nothing to resolve them to.
        return values.map((value) => ({ label: value, value }))
    }
}
